// Helper functions
function getCurrentPageFromQuery() {
  const params = new URLSearchParams(window.location.search);
  const page = parseInt(params.get('page'), 10);
  return isNaN(page) ? 1 : page;
}

function getCurrentViewport() {
  const width = window.innerWidth;
  if (width >= 1024) return 'desktop';
  if (width >= 700) return 'tablet';
  return 'mobile';
}

function getColumnsPerRow(productList, viewport) {
  const explicitColumns = parseInt(productList.getAttribute(`products-per-row-${viewport}`), 10);

  if (!Number.isNaN(explicitColumns) && explicitColumns > 0) {
    return explicitColumns;
  }

  // Keep JS in sync with template-driven layout modes used by main collection.
  if (viewport === 'mobile' || viewport === 'tablet') {
    const mobileLayout = productList.getAttribute('collection-mobile-layout');
    return mobileLayout === 'large' ? 1 : 2;
  }

  const desktopLayout = productList.getAttribute('collection-desktop-layout');
  return desktopLayout === 'large' ? 3 : 4;
}

function calculateEffectiveProductSpace(baseTotalProductsDisplayed) {
  var totalBannerPositions = 0;

  // Sum up space occupied by currently visible banners (in product-cell units)
  document.querySelectorAll('.banner__block:not(.hidden)').forEach(function (banner) {
    var rowSpan = parseInt(banner.getAttribute('row-span'), 10) || 1;
    var colSpan = parseInt(banner.getAttribute('col-span'), 10) || 1;
    totalBannerPositions += rowSpan * colSpan;
  });

  // Also account for promo blocks (.product-list__promo) that take grid space
  document.querySelectorAll('.product-list__promo').forEach(function (promo) {
    var colSpan = parseInt(promo.style.getPropertyValue('--product-list-promo-column-span'), 10) || 1;
    totalBannerPositions += colSpan;
  });

  return baseTotalProductsDisplayed + totalBannerPositions;
}

function calculateGridRows(baseTotalProductsDisplayed, columnsPerRow) {
  var effectiveSpace = calculateEffectiveProductSpace(baseTotalProductsDisplayed);
  return Math.ceil(effectiveSpace / columnsPerRow);
}

function getVisibleAbsoluteRows(baseTotalProductsDisplayed, columnsPerRow, productsPerPage, minPage) {
  var totalRowsVisible = calculateGridRows(baseTotalProductsDisplayed, columnsPerRow);
  var firstAbsoluteRow = Math.ceil(((minPage - 1) * productsPerPage + 1) / columnsPerRow);

  // Ensure the visible range covers ALL banner positions, including those
  // that are currently hidden. Without this, banners positioned beyond the
  // product-only grid (e.g. row 23 on mobile with 2 columns) are never discovered.
  var maxBannerRow = 0;
  document.querySelectorAll('.banner__block[position-row]').forEach(function (banner) {
    var row = parseInt(banner.getAttribute('position-row'), 10);
    if (row > maxBannerRow) maxBannerRow = row;
  });

  var lastCurrentlyVisibleRow = firstAbsoluteRow + totalRowsVisible - 1;
  if (maxBannerRow > lastCurrentlyVisibleRow) {
    totalRowsVisible = maxBannerRow - firstAbsoluteRow + 1;
  }

  return Array.from({ length: totalRowsVisible }, function (_, i) {
    return firstAbsoluteRow + i;
  });
}

/**
 * Checks if a banner can be placed at the given position without exceeding
 * the available grid space. Accounts for space already taken by visible banners
 * and promo blocks.
 */
function isBannerPositionValid(relativeRowIndex, positionColumn, baseTotalProductsDisplayed, columnsPerRow) {
  var effectiveSpace = calculateEffectiveProductSpace(baseTotalProductsDisplayed);
  var elementsUpToPosition = relativeRowIndex * columnsPerRow + positionColumn;
  return elementsUpToPosition <= effectiveSpace;
}

/**
 * Applies CSS grid-row and grid-column styles to a banner.
 */
function applyBannerStyles(banner, relativeRow, positionColumn) {
  var rowSpan = parseInt(banner.getAttribute('row-span'), 10) || 1;
  var colSpan = parseInt(banner.getAttribute('col-span'), 10) || 1;

  banner.style.gridRow = rowSpan > 1 ? relativeRow + ' / span ' + rowSpan : relativeRow;
  banner.style.gridColumn = colSpan > 1 ? positionColumn + ' / span ' + colSpan : positionColumn;

  return { rowSpan: rowSpan, colSpan: colSpan };
}

function insertBannerBlocks() {
  // Update page tracking
  var currentPage = getCurrentPageFromQuery();
  if (window.minPage === null || window.maxPage === null) {
    window.minPage = window.maxPage = currentPage;
  } else {
    window.minPage = Math.min(window.minPage, currentPage);
    window.maxPage = Math.max(window.maxPage, currentPage);
  }

  // Get DOM elements and configuration
  var productList = document.querySelector('.product-list');
  if (!productList) return;

  var viewport = getCurrentViewport();
  var columnsPerRow = getColumnsPerRow(productList, viewport);

  var productsPerPage = parseInt(productList.getAttribute('products-per-page'), 10) || 12;

  var baseTotalProductsDisplayed = productList.querySelectorAll('product-card').length;

  // Reset all banners to hidden and set viewport-specific position attributes
  document.querySelectorAll('.banner__block').forEach(function (banner) {
    banner.classList.add('hidden');

    // Set the current viewport position attributes for easier access
    var positionRow = banner.getAttribute('position-row-' + viewport);
    var positionColumn = banner.getAttribute('position-column-' + viewport);

    if (positionRow && positionColumn) {
      banner.setAttribute('position-row', positionRow);
      banner.setAttribute('position-column', positionColumn);
    } else {
      // If no position for current viewport, ensure banner stays hidden
      banner.removeAttribute('position-row');
      banner.removeAttribute('position-column');
    }
  });

  // Main processing
  var visibleAbsoluteRows = getVisibleAbsoluteRows(
    baseTotalProductsDisplayed,
    columnsPerRow,
    productsPerPage,
    window.minPage,
  );
  insertVisibleBanners(visibleAbsoluteRows);

  function insertVisibleBanners(visibleRows) {
    var bannersInserted = false;

    visibleRows.forEach(function (absoluteRow, index) {
      var bannerBlocks = document.querySelectorAll('.banner__block[position-row="' + absoluteRow + '"]');
      bannerBlocks.forEach(function (banner) {
        var positionColumn = parseInt(banner.getAttribute('position-column'), 10);
        var relativeRow = index + 1;

        if (banner.classList.contains('hidden')) {
          banner.classList.remove('hidden');
          bannersInserted = true;
        }

        applyBannerStyles(banner, relativeRow, positionColumn);
      });
    });

    // If the grid changed, recalculate rows and reinsert
    if (bannersInserted) {
      requestAnimationFrame(function () {
        var newVisibleRows = getVisibleAbsoluteRows(
          baseTotalProductsDisplayed,
          columnsPerRow,
          productsPerPage,
          window.minPage,
        );
        insertVisibleBanners(newVisibleRows);
      });
    }
  }
}
